// ---------------------------------------------------------------------------
// "A new version is ready" — because an open tab keeps running the code it
// loaded, forever.
// ---------------------------------------------------------------------------
// 2026-07-25: Pat reported a bug, I shipped the fix, and he reported it again —
// his tab was still running the bundle from before the deploy, and nothing in the
// app could tell him that. With ~52 people who leave this open all day, every
// release silently splits the company across two versions until each person
// happens to reload.
//
// It NEVER reloads by itself. That lesson is from cg-pos, where an auto-updating
// PWA hard-reloaded the registers mid-shift. Someone halfway through a comment
// decides when to refresh; this only tells them there is something to get.
//
// TWO THINGS MEASURED IN A REAL BROWSER, both of which broke the obvious version:
//  1. A hidden tab's timers are frozen — a 1s interval fired ZERO times in 4s. So
//     the interval is a best-effort extra, and the load-bearing triggers are
//     `focus` and `visibilitychange`: the moment someone comes back to the tab is
//     also the moment they are about to act on stale code.
//  2. `/health` does NOT use the `{success,data}` envelope the rest of the API
//     uses, so `api.get('/health')` throws on it. This reads the body directly
//     and accepts either shape.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { apiOrigin } from '@/core/api/apiClient';

interface Health { version?: string; gitSha?: string }

/** Identity of a running server build. Version alone is not enough — a hotfix can
 *  redeploy the same version, and the git sha is what actually changed. */
export function buildId(h: Health): string {
  return `${h.version ?? '?'}@${h.gitSha ?? '?'}`;
}

/** `/health` answers bare (`{status,version,gitSha}`), unlike every other route
 *  (`{success,data}`). Accept both so this can't be broken by tidying either one. */
export function readHealth(body: unknown): Health | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const src = (b.data && typeof b.data === 'object' ? b.data : b) as Record<string, unknown>;
  const version = typeof src.version === 'string' ? src.version : undefined;
  const gitSha = typeof src.gitSha === 'string' ? src.gitSha : undefined;
  if (!version && !gitSha) return null; // not a health payload — say nothing
  return { version, gitSha };
}

const CHECK_MS = 5 * 60_000;

export function UpdateBanner() {
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // The build this tab is actually running: whatever the server reported on the
    // first successful check after load. Deriving it from the client's own version
    // constant would miss a hotfix that redeploys the same version number.
    let loadedBuild: string | null = null;
    let cancelled = false;

    const check = async (): Promise<void> => {
      try {
        const res = await fetch(`${apiOrigin()}/api/v1/health`, { cache: 'no-store' });
        if (!res.ok || cancelled) return;
        const h = readHealth(await res.json());
        if (!h || cancelled) return;
        const id = buildId(h);
        if (loadedBuild === null) { loadedBuild = id; return; }
        if (id !== loadedBuild) setStale(true);
      } catch {
        // Offline, or the server is mid-restart. Say nothing and try again later —
        // a failed check must never be reported as "you're out of date".
      }
    };

    void check();
    const t = setInterval(() => void check(), CHECK_MS);
    const onVisible = (): void => { if (document.visibilityState === 'visible') void check(); };
    document.addEventListener('visibilitychange', onVisible);
    const onFocus = (): void => { void check(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  if (!stale || dismissed) return null;

  return (
    <div
      role="status"
      className="fixed bottom-5 left-5 z-[80] flex items-center gap-3 rounded-lg border border-brand/40 bg-canvas px-3 py-2 text-[12px] shadow-2xl animate-fade-in"
    >
      <span className="text-ink">A newer version of CG Projects is ready.</span>
      <button
        onClick={() => window.location.reload()}
        className="rounded bg-brand/20 px-2 py-1 font-semibold text-brand hover:bg-brand/30"
      >
        Refresh
      </button>
      <button onClick={() => setDismissed(true)} className="text-muted hover:text-ink" aria-label="Dismiss">
        Later
      </button>
    </div>
  );
}
